(() => {
  'use strict';

  const ROOT_SELECTOR = '[data-ks-faq-root]';

  const normalise = (value) =>
    String(value || '')
      .toLowerCase()
      .replace(/[’']/g, '')
      .replace(/[^a-z0-9$+\-\s]/g, ' ')
      .replace(/\s+/g, ' ')
      .trim();


  const SEARCH_ALIAS_GROUPS = [
    {
      match: ['order status', 'unfulfilled', 'fulfillment accepted', 'quality check'],
      aliases: ['where is my order', 'wheres my order', 'wismo', 'pending order', 'order still processing', 'status meaning'],
    },
    {
      match: ['order confirmation'],
      aliases: ['confirmation missing', 'no confirmation email', 'did not get email', 'order email'],
    },
    {
      match: ['change the item', 'cancel an order', 'shipping address', 'combine separate orders'],
      aliases: ['edit order', 'modify order', 'change size after order', 'change color after order', 'cancel purchase', 'wrong address', 'combine orders'],
    },
    {
      match: ['gift wrapping'],
      aliases: ['gift wrap', 'wrap as gift', 'present wrapping'],
    },
    {
      match: ['shipping upgrade', 'express shipping'],
      aliases: ['rush shipping', 'expedite order', 'faster shipping', 'upgrade delivery', 'pay for faster shipping'],
    },
    {
      match: ['po boxes', 'apo/fpo', 'forwarding addresses'],
      aliases: ['po box', 'p o box', 'apo', 'fpo', 'military address', 'parcel locker', 'mail forwarding', 'freight forwarder', 'reshipper'],
    },
    {
      match: ['can’t an item ship', 'cannot an item ship', 'ship to my address'],
      aliases: ['cannot ship to me', 'address not accepted', 'checkout blocks address', 'destination restricted', 'item unavailable in country'],
    },
    {
      match: ['where do orders ship from', 'us stock'],
      aliases: ['usa stock', 'us warehouse', 'domestic warehouse', 'ships from america', 'local stock'],
    },
    {
      match: ['refuse a delivery', 'returned to sender'],
      aliases: ['refused package', 'reject delivery', 'send package back', 'rts', 'return to sender'],
    },
    {
      match: ['track my package', 'tracking stopped', 'label created', 'delivered, but'],
      aliases: ['where is my package', 'tracking stuck', 'no tracking update', 'package not moving', 'delivered not received', 'marked delivered missing'],
    },
    {
      match: ['return window', 'start a return', 'return address', 'rma', 'refund options'],
      aliases: ['send it back', 'return item', 'return authorization', 'exchange item', 'restocking fee', 'store credit', 'refund amount'],
    },
    {
      match: ['wrong, damaged', 'missing from my package', 'materially different', 'tampered'],
      aliases: ['wrong item', 'broken item', 'defective product', 'missing piece', 'package empty', 'see through', 'too transparent', 'not as pictured', 'photo mismatch', 'different material'],
    },
    {
      match: ['size chart', 'choose a size', 'fits much smaller', 'ordered the wrong size'],
      aliases: ['runs small', 'runs large', 'wrong fit', 'size tag correct', 'measurements wrong', 'asian sizing', 'sizing help'],
    },
    {
      match: ['cosplay', 'made-to-order', 'event'],
      aliases: ['costume', 'convention', 'halloween', 'production time', 'custom order', 'need by date'],
    },
    {
      match: ['payment declined', 'verify my order', 'discount code'],
      aliases: ['card declined', 'security review', 'fraud check', 'order verification', 'confirm purchase', 'coupon not working', 'promo code'],
    },
    {
      match: ['kawaii shield'],
      aliases: ['shipping protection', 'shipping insurance', 'package protection', 'stolen package claim', 'lost package claim'],
    },
    {
      match: ['contact customer support', 'phone support'],
      aliases: ['customer service', 'support email', 'contact number', 'phone number', '855 kawaii1', '855 529 2441'],
    },
    {
      match: ['not received a reply'],
      aliases: ['no response', 'support did not reply', 'follow up', 'waiting for support', 'no email back'],
    },
  ];

  const searchableWithAliases = (item) => {
    const base = normalise(item.dataset.search || item.textContent);
    const aliases = [];

    SEARCH_ALIAS_GROUPS.forEach((group) => {
      if (group.match.some((term) => base.includes(normalise(term)))) {
        aliases.push(...group.aliases);
      }
    });

    return `${base} ${normalise(aliases.join(' '))}`.trim();
  };

  const initHelpCenter = (root) => {
    if (!root || root.dataset.ksFaqReady === 'true') return;

    const controller = new AbortController();
    const { signal } = controller;

    const search = root.querySelector('[data-ks-faq-search]');
    const clear = root.querySelector('[data-ks-faq-clear]');
    const reset = root.querySelector('[data-ks-faq-reset]');
    const status = root.querySelector('[data-ks-faq-status]');
    const empty = root.querySelector('[data-ks-faq-empty]');
    const topicButtons = Array.from(
      root.querySelectorAll('[data-ks-faq-topic]'),
    );
    const groups = Array.from(
      root.querySelectorAll('[data-ks-faq-group]'),
    );
    const items = Array.from(
      root.querySelectorAll('[data-ks-faq-item]'),
    );

    let activeTopic = 'all';

    const updateStatus = (count) => {
      if (!status) return;
      status.textContent =
        count === 1 ? '1 answer shown' : `${count} answers shown`;
    };

    const applyFilter = () => {
      const query = normalise(search?.value);
      let visibleCount = 0;

      items.forEach((item) => {
        const topicMatch =
          activeTopic === 'all' ||
          item.dataset.topic === activeTopic;
        const searchable = searchableWithAliases(item);
        const queryMatch =
          !query ||
          query
            .split(' ')
            .filter(Boolean)
            .every((term) => searchable.includes(term));
        const visible = topicMatch && queryMatch;

        item.hidden = !visible;
        if (!visible) item.open = false;
        if (visible) visibleCount += 1;
      });

      groups.forEach((group) => {
        const hasVisibleItem = Array.from(
          group.querySelectorAll('[data-ks-faq-item]'),
        ).some((item) => !item.hidden);

        group.hidden = !hasVisibleItem;
      });

      if (empty) empty.hidden = visibleCount !== 0;
      if (clear) clear.hidden = !query;
      updateStatus(visibleCount);
    };

    const setTopic = (topic) => {
      activeTopic = topic || 'all';

      topicButtons.forEach((button) => {
        button.setAttribute(
          'aria-pressed',
          button.dataset.ksFaqTopic === activeTopic
            ? 'true'
            : 'false',
        );
      });

      applyFilter();
    };

    const resetFilters = ({ focus = true } = {}) => {
      activeTopic = 'all';
      if (search) search.value = '';
      setTopic('all');
      if (focus && search) search.focus();
    };

    search?.addEventListener('input', applyFilter, { signal });

    search?.addEventListener(
      'keydown',
      (event) => {
        if (event.key === 'Escape' && search.value) {
          event.preventDefault();
          resetFilters();
        }
      },
      { signal },
    );

    clear?.addEventListener(
      'click',
      () => {
        if (search) search.value = '';
        applyFilter();
        search?.focus();
      },
      { signal },
    );

    reset?.addEventListener(
      'click',
      () => resetFilters(),
      { signal },
    );

    topicButtons.forEach((button) => {
      button.addEventListener(
        'click',
        () => setTopic(button.dataset.ksFaqTopic),
        { signal },
      );
    });

    items.forEach((item) => {
      item.addEventListener(
        'toggle',
        () => {
          if (!item.open) return;

          const group = item.closest('[data-ks-faq-group]');
          group
            ?.querySelectorAll('[data-ks-faq-item][open]')
            .forEach((other) => {
              if (other !== item) other.open = false;
            });

          if (item.id) {
            try {
              window.history.replaceState(
                null,
                '',
                `${window.location.pathname}${window.location.search}#${item.id}`,
              );
            } catch (_error) {
              // History may be unavailable in a local Theme Editor preview.
            }
          }
        },
        { signal },
      );
    });

    root.querySelectorAll('[data-ks-faq-copy]').forEach((button) => {
      button.addEventListener(
        'click',
        async () => {
          const anchor = button.dataset.copyAnchor;
          if (!anchor) return;

          const link = `${window.location.origin}${window.location.pathname}#${anchor}`;
          const original = button.textContent;
          const statusText = button.parentElement?.querySelector('.ks-faq-v3__copy-status');

          try {
            if (navigator.clipboard?.writeText) {
              await navigator.clipboard.writeText(link);
            } else {
              const field = document.createElement('textarea');
              field.value = link;
              field.setAttribute('readonly', '');
              field.style.position = 'fixed';
              field.style.opacity = '0';
              document.body.appendChild(field);
              field.select();
              document.execCommand('copy');
              field.remove();
            }

            button.textContent = 'Link copied';
            button.classList.add('is-copied');
            if (statusText) statusText.textContent = 'Direct answer link copied to clipboard.';
            window.setTimeout(() => {
              button.textContent = original;
              button.classList.remove('is-copied');
            }, 1800);
          } catch (_error) {
            window.location.hash = anchor;
            button.textContent = 'Link ready in address bar';
            if (statusText) statusText.textContent = 'The direct answer link is now in the address bar.';
          }
        },
        { signal },
      );
    });

    const openHashTarget = () => {
      const hash = window.location.hash;
      if (!hash) return;

      let target;
      try {
        target = root.querySelector(hash);
      } catch (_error) {
        return;
      }

      if (!target) return;

      if (target.matches('[data-ks-faq-item]')) {
        resetFilters({ focus: false });
        target.hidden = false;
        target.closest('[data-ks-faq-group]')?.removeAttribute('hidden');
        target.open = true;
      }
    };

    window.addEventListener('hashchange', openHashTarget, { signal });

    root.ksFaqController = controller;
    root.dataset.ksFaqReady = 'true';

    applyFilter();
    openHashTarget();
  };

  const initAll = (scope = document) => {
    const roots =
      scope.matches?.(ROOT_SELECTOR)
        ? [scope]
        : Array.from(scope.querySelectorAll?.(ROOT_SELECTOR) || []);

    roots.forEach(initHelpCenter);
  };

  if (document.readyState === 'loading') {
    document.addEventListener(
      'DOMContentLoaded',
      () => initAll(document),
      { once: true },
    );
  } else {
    initAll(document);
  }

  document.addEventListener('shopify:section:load', (event) => {
    initAll(event.target);
  });

  document.addEventListener('shopify:section:unload', (event) => {
    const root = event.target.querySelector?.(ROOT_SELECTOR);
    root?.ksFaqController?.abort();
  });
})();
